import type { ReactNode } from "react";
import { Checkbox } from "@pack-it-up/ui-web";
import { canSelfComplete, isItemComplete, packingStateOf, participantsOf, relevantParticipants, type Completable, type ParticipantContext } from "@pack-it-up/core";
import type { ParticipantId, UserId } from "@pack-it-up/shared";

type Toggle = (target: ParticipantId, targetIsUser: boolean) => void;

// Anything with per-participant completion + a name (packing items and to-dos both).
type Row = Completable & { name: string };

// At most one checkbox per row (Doc 06 §3–6 / gap-analysis T-E), plus an assignment
// visual (rendered separately in TripDetailPage — see AssignmentChips):
// - shared: single checkbox toggles the shared completion; a tooltip (not visible text)
//   names who checked it.
// - everyone/specific where the current user is a relevant participant: single checkbox
//   toggles ONLY the actor's own participant entry.
// - everyone/specific where the current user is not relevant (e.g. a `specific` item
//   assigned to other people only): no checkbox — a 16px placeholder keeps names aligned.
export const CompletionControl = ({
  item,
  list,
  actor,
  canEdit,
  // T27: when true, the row shows the 3-way staged/packed cycle instead of the plain
  // boolean (only meaningful for packing items — never passed true for to-dos).
  stagingEnabled = false,
  onToggle,
  // Wording differs by kind: items are "packed", to-dos are "done".
  imperative = "Pack",
  pastTense = "packed",
}: {
  item: Row;
  // The trip or template the row belongs to (issue #188) — only its participants and
  // proxy-completion setting are read.
  list: ParticipantContext;
  // Absent for a link-grant viewer (Doc 08 §4a) — there's no "my own entry" to read, so
  // the box falls back to the aggregate (below) instead of a personal one, always disabled.
  actor?: UserId;
  canEdit: boolean;
  stagingEnabled?: boolean;
  onToggle: Toggle;
  imperative?: string;
  pastTense?: string;
}): ReactNode => {
  if (actor === undefined) {
    const complete = isItemComplete(item, participantsOf(list));
    return <Checkbox checked={complete} disabled aria-label={`${imperative} ${item.name}`} onChange={() => {}} />;
  }

  // packingStateOf reads the ACTOR'S OWN entry (for shared, it ignores the id and
  // reads the single collapsed record) — the checkbox has always shown "did I check
  // this?", never an aggregate across other assignees (that aggregate already exists
  // separately as `isItemComplete`, used for row-complete styling/sort/filter).
  // Collapses to "unstaged"/"packed" only when stagingEnabled is false, so this is the
  // single state source for both the legacy boolean box and T27's 3-way cycle.
  const state = packingStateOf(item, actor as ParticipantId, stagingEnabled);
  const packed = state === "packed";

  if (item.assignment.kind === "shared") {
    // Shared reads as done if anyone is marked; show the first such checker in a tooltip.
    const doneEntry = Object.values(item.completions.byParticipant).find((e) => e.done);
    const by = doneEntry?.checkedBy;
    const whoName = by !== undefined ? list.members[by as ParticipantId]?.profile.displayName : undefined;
    const title = packed && whoName !== undefined ? `${pastTense[0]?.toUpperCase()}${pastTense.slice(1)} by ${whoName}` : undefined;
    return (
      <span title={title}>
        <Checkbox checked={packed} state={state} disabled={!canEdit} aria-label={`${imperative} ${item.name}`} onChange={() => onToggle(actor as ParticipantId, true)} />
      </span>
    );
  }

  if (!canSelfComplete(item, list, actor)) {
    // Proxy completion (T23 §2): the actor isn't a relevant participant, but with a
    // single one and proxy on, a checkbox targeting that person is unambiguous — no
    // need to fall back to the trailing AssignmentChips just to check one box.
    const relevant = list.settings.allowProxyCompletion ? relevantParticipants(item, list) : [];
    if (relevant.length === 1) {
      const target = relevant[0]!;
      const targetIsUser = list.members[target]?.kind === "user";
      const targetName = list.members[target]?.profile.displayName;
      const targetState = packingStateOf(item, target, stagingEnabled);
      return (
        <Checkbox
          checked={targetState === "packed"}
          state={targetState}
          disabled={!canEdit}
          aria-label={`${imperative} ${item.name}${targetName !== undefined ? ` for ${targetName}` : ""}`}
          onChange={() => onToggle(target, targetIsUser)}
        />
      );
    }
    // Hidden checkbox: reserve the same 16px width so item names still align across rows
    // that do show a checkbox (kit `.checkbox` is 16x16).
    return <span aria-hidden style={{ display: "inline-block", width: "16px", height: "16px", flexShrink: 0 }} />;
  }

  return (
    <Checkbox
      checked={packed}
      state={state}
      disabled={!canEdit}
      aria-label={`${imperative} ${item.name}`}
      onChange={() => onToggle(actor as ParticipantId, true)}
    />
  );
};
